<template>
    <div>
        <div class="box view-nav-bar">
            <div class="d-flex align-items-center w-100">
                <div class="flex-grow-1">
                    <router-link to="/admissions/movie" class="mr-3">
                        <icon name="chevron-left"/> Back
                    </router-link>
                    <strong>Per movie</strong>
                    <span class="text-muted ml-2">— cinema year <strong>{{ year }}</strong>, top {{ bucket.top || 100 }} movies, all sessions vs. Brightfish preshow</span>
                </div>
                <div class="form-inline">
                    <label class="text-muted mr-2" for="tag-filter">Tag</label>
                    <select id="tag-filter" v-model="tag" class="form-control form-control-sm mr-3" :disabled="loading || !tags.length">
                        <option value="">All tags</option>
                        <option v-for="t in tags" :key="t.movie_tag || 'none'" :value="t.movie_tag || 'none'">
                            {{ tagLabel(t.movie_tag) }} ({{ t.movies }} movies, {{ formatInt(t.admissions) }})
                        </option>
                    </select>
                    <router-link :to="{ path: '/admissions/movie/year/' + (parseInt(year, 10) - 1), query: $route.query }" class="btn btn-sm btn-outline-secondary mr-2">
                        <icon name="chevron-left"/> {{ parseInt(year, 10) - 1 }}
                    </router-link>
                    <router-link :to="{ path: '/admissions/movie/year/' + (parseInt(year, 10) + 1), query: $route.query }" class="btn btn-sm btn-outline-secondary">
                        {{ parseInt(year, 10) + 1 }} <icon name="chevron-right"/>
                    </router-link>
                </div>
            </div>
        </div>

        <div v-if="loading" class="box">
            <div class="box-content text-center text-muted py-4">Loading…</div>
        </div>

        <div v-else-if="!totals" class="box">
            <div class="box-content text-center text-muted py-4">No admissions in cinema year {{ year }}.</div>
        </div>

        <template v-else>
            <div class="box">
                <div class="box-content">
                    <table class="shx-table w-100 mt-2">
                        <thead>
                            <tr>
                                <th></th>
                                <th class="text-right">Admissions</th>
                                <th class="text-right shx-th-120">Avg / week</th>
                                <th class="text-right shx-th-120">% of all</th>
                                <th class="text-right shx-th-100"># Weeks</th>
                                <th class="text-right shx-th-100"># Cinemas</th>
                                <th class="text-right shx-th-100"># Movies</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>All sessions</strong> <span class="text-muted small ml-2">every title, every sales house</span></td>
                                <td class="text-right">{{ formatInt(totals.admissions) }}</td>
                                <td class="text-right">{{ totals.weeks ? formatInt(totals.admissions / totals.weeks) : '—' }}</td>
                                <td class="text-right">100%</td>
                                <td class="text-right">{{ totals.weeks }}</td>
                                <td class="text-right">{{ totals.cinemas }}</td>
                                <td class="text-right">{{ totals.movies }}</td>
                            </tr>
                            <tr>
                                <td><strong>Preshow sessions</strong> <span class="text-muted small ml-2">sales house BF</span></td>
                                <td class="text-right">{{ formatInt(totals.admissions_preshow) }}</td>
                                <td class="text-right">{{ totals.weeks ? formatInt(totals.admissions_preshow / totals.weeks) : '—' }}</td>
                                <td class="text-right">{{ totals.pct_preshow !== null ? totals.pct_preshow + '%' : '—' }}</td>
                                <td colspan="3"></td>
                            </tr>
                            <tr v-if="totals.tag">
                                <td><strong>Tag {{ tagLabel(totals.tag.movie_tag) }}</strong> <span class="text-muted small ml-2">all sales houses</span></td>
                                <td class="text-right">{{ formatInt(totals.tag.admissions) }}</td>
                                <td class="text-right">{{ totals.weeks ? formatInt(totals.tag.admissions / totals.weeks) : '—' }}</td>
                                <td class="text-right">{{ totals.tag.pct !== null ? totals.tag.pct + '%' : '—' }}</td>
                                <td colspan="2"></td>
                                <td class="text-right">{{ totals.tag.movies }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div class="box">
                <div class="box-content">
                    <heading upper="Top movies"
                             :lower="'Top ' + rows.length + ' of ' + (totals.tag ? totals.tag.movies + ' ' + tagLabel(totals.tag.movie_tag) : totals.movies) + ' movies by admissions in ' + year + ', all sales houses'"
                             border/>
                    <p v-if="tag && !rows.length" class="text-muted small mt-2 mb-0">No movies with tag {{ tagLabel(tag === 'none' ? null : tag) }} in {{ year }}.</p>
                    <table v-else class="shx-table w-100 mt-2">
                        <thead>
                            <tr>
                                <th class="shx-th-50">#</th>
                                <th class="shx-th-80">Movix</th>
                                <th>Title</th>
                                <th class="shx-th-120">Tag</th>
                                <th class="text-right shx-th-100"># Weeks</th>
                                <th class="text-right">Admissions</th>
                                <th class="text-right shx-th-100">% of year</th>
                                <th class="text-right shx-th-100">Cumul. %</th>
                                <th class="text-right shx-th-120">Preshow (BF)</th>
                                <th class="text-right shx-th-120">% of preshow</th>
                                <th class="text-right shx-th-100">Real %</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(movie, index) in rows" :key="movie.movix_id">
                                <td class="text-muted">{{ index + 1 }}</td>
                                <td>
                                    <router-link :to="'/admissions/movie/' + movie.movix_id">{{ movie.movix_id }}</router-link>
                                </td>
                                <td>
                                    <router-link :to="'/admissions/movie/' + movie.movix_id">{{ movie.title || '—' }}</router-link>
                                </td>
                                <td>
                                    <a href="#" :class="{ 'text-muted': !movie.movie_tag }" @click.prevent="tag = movie.movie_tag || 'none'">{{ tagLabel(movie.movie_tag) }}</a>
                                </td>
                                <td class="text-right">{{ movie.weeks }}</td>
                                <td class="text-right">{{ formatInt(movie.admissions) }}</td>
                                <td class="text-right">{{ movie.pct !== null ? movie.pct + '%' : '—' }}</td>
                                <td class="text-right text-muted">{{ pct(cumulative[index], totals.admissions) }}</td>
                                <td class="text-right">{{ formatInt(movie.admissions_preshow) }}</td>
                                <td class="text-right">{{ movie.pct_preshow !== null ? movie.pct_preshow + '%' : '—' }}</td>
                                <td class="text-right">
                                    <span>{{ movie.real_weighted != null ? movie.real_weighted + '%' : '—' }}</span>
                                    <span v-if="movie.real_weighted != null && movie.real_weighted <= 90"
                                          class="badge badge-warning ml-1">⚠</span>
                                </td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colspan="5"><strong>Top {{ rows.length }}</strong></td>
                                <td class="text-right"><strong>{{ formatInt(topAdmissions) }}</strong></td>
                                <td class="text-right"><strong>{{ pct(topAdmissions, totals.admissions) }}</strong></td>
                                <td></td>
                                <td class="text-right"><strong>{{ formatInt(topPreshow) }}</strong></td>
                                <td class="text-right"><strong>{{ pct(topPreshow, totals.admissions_preshow) }}</strong></td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </table>
                    <p class="text-muted small mt-2 mb-0">
                        <strong>Admissions</strong> = every <code>admissions_week</code> row of the movie, whatever the sales house;
                        <strong>Preshow</strong> = rows with <code>saleshouse = BF</code>. The year totals include titles without
                        a Movix id, so the shares are of everything reported in {{ year }}, also when filtered on a tag.
                        <strong>Tag</strong> is the Movix target pack from <code>movix_history</code>; click one to filter.
                        <strong>Real %</strong> is the
                        admissions-weighted average of <code>real</code>: 97.5 = fully compiled from Cinedata, lower = partly estimated.
                        Same data as JSON: <code>GET /api/v1/admissions/movie/year/{{ year }}<span v-if="tag">?tag={{ tag }}</span></code>.
                    </p>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import axios from 'axios';
import Heading from '../../partials/Heading';

export default {
    name: 'AdmissionsMovieYear',
    components: {
        Heading,
    },
    props: ['year'],
    data() {
        return {
            loading: true,
            bucket: {},
            totals: null,
            rows: [],
            tag: this.$route.query.tag || '',
        };
    },
    computed: {
        tags() {
            return this.bucket.tags || [];
        },
        topAdmissions() {
            return this.rows.reduce((sum, m) => sum + (m.admissions || 0), 0);
        },
        topPreshow() {
            return this.rows.reduce((sum, m) => sum + (m.admissions_preshow || 0), 0);
        },
        cumulative() {
            let running = 0;
            return this.rows.map(m => (running += m.admissions || 0));
        },
    },
    created() {
        this.load();
    },
    watch: {
        year() {
            this.load();
        },
        tag(value) {
            const query = value ? { tag: value } : {};
            if ((this.$route.query.tag || '') !== value) {
                this.$router.replace({ path: this.$route.path, query });
            }
            this.load();
        },
        '$route.query.tag'(value) {
            this.tag = value || '';
        },
    },
    methods: {
        load() {
            this.loading = true;
            axios
                .get(`/admissions/movie/year/${this.year}`, { params: this.tag ? { tag: this.tag } : {} })
                .then(response => {
                    this.bucket = response.data.data.bucket || {};
                    this.totals = response.data.data.totals || null;
                    this.rows = response.data.data.rows || [];
                })
                .catch(error => {
                    this.$toast.error(error.response?.data?.error?.message || error.message);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        tagLabel(tag) {
            return tag || 'no tag';
        },
        pct(part, total) {
            return total ? (Math.round(part / total * 1000) / 10) + '%' : '—';
        },
        formatInt(v) {
            return Math.round(v || 0).toLocaleString();
        },
    },
};
</script>
